import { useMemo, useState, useCallback } from "react";
import type { LayoutChangeEvent } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { Card, Text, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { IconButton } from "@src/components/IconButton";
import { ClipVideoPlayer, downloadVideo, handleDownloadError } from "@src/features/clips";
import type { Clip } from "@src/features/clips";
import { useClips, type ClipItem } from "@visioni/a3s-media/app";
import { toISODateString } from "@visioni/a3s-codecs";
import { SiteId, AssetCameraKey } from "@visioni/a3s-schema";
import type { WasteSenseDumpDetection } from "@visioni/a3s-reports/client-messages";

interface DumpDetectionsProps {
  dumps: readonly WasteSenseDumpDetection[];
}

/**
 * "Dump Detections" section — operator-confirmed dumps, each with its evidence
 * clip. Rendered FIRST in the report (before the weekly summary) so the most
 * important information leads.
 *
 * Clip playback reuses the media `useClips` → `ClipVideoPlayer` path (mirroring
 * `DumpingSearch.web.tsx`): the payload carries clip *identifiers*
 * (`site_id` + `asset_camera_key` + `clip_timestamp`), and each card resolves a
 * short-lived presigned URL at view time. We deliberately do NOT bake a URL
 * into the stored report — presigned URLs expire.
 */
export function DumpDetections({ dumps }: DumpDetectionsProps) {
  return (
    <View gap="md">
      <Text textStyle="h4" color="text">
        Dump Detections
      </Text>

      {dumps.length === 0 ?
        <Card px="md" py="xl" alignItems="center">
          <Text textStyle="sm/regular" color="textMuted">
            No dumps detected
          </Text>
        </Card>
      : <View gap="md">
          {dumps.map((dump) => (
            <DumpCard key={dump.dump_id} dump={dump} />
          ))}
        </View>
      }
    </View>
  );
}

/**
 * Adapt a media-service {@link ClipItem} to the clips feature's {@link Clip} so
 * the report can reuse {@link ClipVideoPlayer}. Mirrors the adapter in
 * `DumpingSearch.web.tsx`: `video.url` is the canonical source, `videoUrl` the
 * deprecated fallback; the clip's start time doubles as its id/timestamp.
 */
function clipItemToClip(item: ClipItem): Clip {
  return {
    id: item.startTime,
    timestamp: item.startTime,
    thumbnailUrl: item.thumbnailUrl ?? null,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- fallback for older deployed apps
    videoUrl: item.video?.url ?? item.videoUrl ?? "",
    duration: item.durationMs,
  };
}

interface DumpCardProps {
  dump: WasteSenseDumpDetection;
}

// Bounded width for the clip column on wide viewports. The shared
// `ClipVideoPlayer` hardcodes its box to `width:100% / aspectRatio 16:9 /
// maxHeight screenHeight*0.4` and sizes off the `screenWidth`/`screenHeight`
// props (not its real parent), so we (a) cap the column so a full-width card
// can't stretch the 16:9 box into the letterbox-heavy shape that looked cropped
// before, and (b) feed the player a `screenWidth` equal to this column and a
// tall `screenHeight` so its maxHeight cap never trims the frame. `contentFit`
// is already "contain", so the whole clip is always shown — nothing is cropped.
const CLIP_COLUMN_WIDTH = 360;
// Card horizontal padding — must track the `px="md"` on the <Card> below so the
// stacked clip's width math subtracts the right inset (spacing token md = 8px).
const CARD_PADDING_X = 8;
// Below this *card* width we stack (clip on top, meta below) rather than sit
// side-by-side: the clip needs CLIP_COLUMN_WIDTH and the metadata needs enough
// room left over to stay legible (labels like "DESCRIPTION" must not wrap
// mid-word). Measured on the card itself — NOT the window — so it reacts to the
// real available space (sidebar open/closed, split panes), not the raw viewport.
const SIDE_BY_SIDE_MIN_WIDTH = CLIP_COLUMN_WIDTH + 220;
// Glyph size (px) for the download icon. A literal size, not a spacing token —
// icon dimensions are a glyph/typographic concern, and this mirrors the sibling
// `CameraSnapshot` (SIZE_PLAY_ICON = 14). Sits comfortably in the 32px "sm"
// IconButton.
const DOWNLOAD_ICON_SIZE = 18;

function DumpCard({ dump }: DumpCardProps) {
  // Measure the card's own width so the row→column flip responds to the actual
  // content area, not the browser window (the report sits beside a sidebar, so
  // window width overstates the room a card really has).
  const [cardWidth, setCardWidth] = useState<number | null>(null);
  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setCardWidth(e.nativeEvent.layout.width);
  }, []);
  // Until first measure, assume side-by-side (the common desktop case) to avoid
  // a stacked→row flash on wide screens.
  const sideBySide = cardWidth === null || cardWidth >= SIDE_BY_SIDE_MIN_WIDTH;

  const parsedSiteId = useMemo(() => SiteId.fromString(dump.site_id), [dump.site_id]);
  const parsedCameraKey = useMemo(() => AssetCameraKey.fromString(dump.asset_camera_key), [dump.asset_camera_key]);
  // The clip identity is its exact start time. `useClips` range-queries with an
  // inclusive `between`, so a zero-width [t, t] window returns the single clip
  // whose startTime === clip_timestamp (the confirmed-dump clip).
  const normalizedTimestamp = useMemo(() => toISODateString(new Date(dump.clip_timestamp)), [dump.clip_timestamp]);

  const {
    data: clips,
    isPending,
    isError,
  } = useClips({
    siteId: parsedSiteId,
    assetCameraKey: parsedCameraKey,
    startTime: normalizedTimestamp,
    endTime: normalizedTimestamp,
  });

  // Match the exact clip by start time; fall back to the first returned clip in
  // case of sub-millisecond rounding differences in the range query.
  const playerClip = useMemo(() => {
    if (!clips || clips.length === 0) return null;
    const exact = clips.find((c) => c.startTime === dump.clip_timestamp) ?? clips[0];
    return exact ? clipItemToClip(exact) : null;
  }, [clips, dump.clip_timestamp]);

  const { theme } = useUnistyles();

  // Download the evidence clip. Mirrors `useClipsLogic.handleDownload`: on web
  // `downloadVideo` opens the (short-lived, resolved-at-view-time) presigned URL
  // in a new tab and toasts; failures route through `handleDownloadError`. The
  // `isDownloading` guard both disables the button and blocks re-entrancy while
  // a download is in flight.
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownload = useCallback(async () => {
    if (!playerClip?.videoUrl || isDownloading) return;
    try {
      setIsDownloading(true);
      await downloadVideo({
        videoUrl: playerClip.videoUrl,
        timestamp: new Date(dump.clip_timestamp),
      });
    } catch (err: unknown) {
      handleDownloadError(err);
    } finally {
      setIsDownloading(false);
    }
  }, [playerClip?.videoUrl, isDownloading, dump.clip_timestamp]);

  return (
    <Card px="md" py="md" onLayout={onLayout}>
      <View flexDirection={sideBySide ? "row" : "column"} gap="md" alignItems={sideBySide ? "flex-start" : "stretch"}>
        {/* Evidence clip — LEFT on wide viewports, on TOP when stacked. Bounded
            width so the whole frame shows (contentFit=contain, never cropped).
            Paused on the first frame so a scan holds still; the reviewer hits
            play to inspect the dump in motion. */}
        <View style={sideBySide ? { width: CLIP_COLUMN_WIDTH, flexShrink: 0 } : { width: "100%" }}>
          {
            playerClip ?
              <ClipVideoPlayer
                key={playerClip.id}
                clip={playerClip}
                autoPlay={false}
                // Feed the player the clip-column width and a tall height so its
                // internal `maxHeight: screenHeight*0.4` cap never trims a frame.
                // Stacked: the clip fills the card (measured width minus px="md"
                // padding on both sides), falling back to the column width pre-measure.
                screenWidth={sideBySide ? CLIP_COLUMN_WIDTH : (cardWidth ?? CLIP_COLUMN_WIDTH) - CARD_PADDING_X * 2}
                screenHeight={CLIP_COLUMN_WIDTH * 3}
                selectedHour={0}
              />
              // Only show "Loading" while the query is genuinely in flight. Once
              // it has settled without a clip — empty result (clip deleted / bad
              // key) or an API error — show "unavailable" instead of spinning.
            : isPending ?
              <View py="xl" alignItems="center">
                <Text textStyle="xs/regular" color="textMuted">
                  Loading clip…
                </Text>
              </View>
            : <View py="xl" alignItems="center">
                <Text textStyle="xs/regular" color="textMuted">
                  {isError ? "Clip unavailable" : "No clip found"}
                </Text>
              </View>

          }
        </View>

        {/* Metadata — RIGHT of the clip on wide viewports, below when stacked. */}
        <View flex={sideBySide ? 1 : undefined} gap="sm" style={sideBySide ? { minWidth: 0 } : undefined}>
          {/* Header row: meta fields on the left, a top-right download control.
              The button only appears once a clip has resolved — there is nothing
              to download otherwise (loading / missing / errored clip). */}
          <View flexDirection="row" gap="md" justifyContent="space-between" alignItems="flex-start">
            <View gap="sm" style={{ minWidth: 0 }}>
              {/* Site is shown so the leading list is legible on multi-site
                  reports — each payload row carries site_name; a single-site
                  report just repeats the one site, which is harmless. */}
              <Meta label="Site" value={dump.site_name} />
              <Meta label="Detected" value={dump.detected_at_label} />
              <Meta label="Object" value={dump.object_label} />
            </View>

            {playerClip ?
              <IconButton
                size="sm"
                title="Download clip"
                onPress={handleDownload}
                disabled={isDownloading}
                Icon={() => <Icon name="download" size={DOWNLOAD_ICON_SIZE} color={theme.colors.text} />}
              />
            : null}
          </View>

          {dump.description !== "" ?
            <View gap="xxs">
              <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
                Description
              </Text>
              <Text textStyle="sm/regular" color="text">
                {dump.description}
              </Text>
            </View>
          : null}
        </View>
      </View>
    </Card>
  );
}

interface MetaProps {
  label: string;
  value: string;
}

function Meta({ label, value }: MetaProps) {
  return (
    <View gap="xxs">
      <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
        {label}
      </Text>
      <Text textStyle="sm/semibold" color="text">
        {value}
      </Text>
    </View>
  );
}
